import type { Translation } from "../i18n-types.js"

const ca = {
  common: {
    countViews: "{{0 visualitzacions|1 visualització|?? visualitzacions}}",
    countReactions: "{{0 reaccions|1 reacció|?? reaccions}}",
    back: "Tornar",
    blog: "Articles",
    settings: "Configuració",
    addPosts: "Crea un article",
    save: "Desar",
    filter: "Filtre",
    tag: "Etiqueta",
    sort: "Ordenar",
    all: "Tots",
    loading: "Carregant...",
    saving: "Desant...",
    delete: "Eliminar",
    edit: "Editar",
    close: "Tancar",
    error: "Error en desar les dades",
    networkError: "Error en desar les dades. Si us plau, comprova la connexió",
    validationError: "Si us plau, revisa el formulari",
    by: "per",
    cancel: "Cancel·lar",
    private: "Privat",
    holdToConfirm: "Mantén per confirmar",
    done: "Fet!",
    defaultLanguage: "per defecte",
    translating: "Traduint...",
    translateAll: "Traduir-ho tot",
    translateFrom: "Traduir des de {language}",
  },
  mainLayout: {
    errorLoadingData: "Hi ha hagut un error en carregar les dades d'aquesta pàgina. Si us plau, torna-ho a provar.",
    reloadPage: "Recarrega la pàgina",
  },
  dashboard: {
    welcomeBack: "Benvingut/da de nou",
    latestPosts: "Últims articles",
    viewAllPosts: "Veure tots",
    noPostsYet: "Encara no has publicat cap article.",
    latestDrafts: "Últims esborranys",
    viewAllDrafts: "Veure tots",
    noDrafts: "No tens cap esborrany",
    latestNotes: "Últimes notes",
    noNotesYet: "Encara no tens cap nota",
    subscribers: "Subscriptors",
    posts: "Articles",
    views: "Visualitzacions",
    openRate: "Taxa d'obertura",
    published: "Publicat",
    created: "Creat",
    recentReactions: "Reaccions recents",
    noReactionsYet: "Encara no tens reaccions",
    reactedToYourPost: "han reaccionat a la teva publicació",
    othersCount: "{{cap altra|1 altra persona|?? altres persones}}",
    createPublication: {
      title: "Crea la teva pròpia publicació",
      description: "Comença el teu propi racó al web. comparteix els teus pensaments, construeix la teva audiència i posseeix el teu contingut.",
      button: "Comença",
    },
    verifyEmail: {
      title: "Verifica el teu correu",
      description: "Verifica la teva adreça de correu electrònic per desbloquejar totes les funcions, com crear la teva pròpia publicació.",
      button: "Verificar correu",
    },
  },
  settings: {
    title: "Configuració",
    localizationTitle: "Localització",
    localizationDescription: "Configura el teu idioma preferit",
    interfaceLanguageLabel: "Idioma de la interfície",
    authenticationTitle: "Autenticació",
    authenticationDescription: "Gestiona el teu compte",
    signedInAs: "Sessió iniciada com {email}",
    signOut: "Tanca la sessió",
    signingOut: "Tancant sessió...",
    authorProfileTitle: "Perfil d'autor",
    authorProfileDescription: "Informació sobre tu que es mostrarà a la teva publicació",
    profileImageLabel: "Foto de perfil (120×120)",
    authorNameLabel: "Nom de l'autor",
    authorNamePlaceholder: "El teu nom",
    bioLabel: "Bio",
    bioPlaceholder: "Explica als teus lectors sobre tu",
    imageSaved: "Imatge pujada",
    imageDeleted: "Imatge eliminada",
    informationSaved: "Desat correctament",
    social: {
      title: "xarxes socials",
      description: "connecta els teus perfils socials a la teva publicació",
      social_x: {
        label: "X (Twitter)",
        placeholder: "https://x.com/nomdusuari",
      },
      social_instagram: {
        label: "Instagram",
        placeholder: "https://instagram.com/nomdusuari",
      },
      social_tiktok: {
        label: "TikTok",
        placeholder: "https://tiktok.com/@nomdusuari",
      },
      social_youtube: {
        label: "YouTube",
        placeholder: "https://youtube.com/@nomdusuari",
      },
      social_facebook: {
        label: "Facebook",
        placeholder: "https://facebook.com/nomdusuari",
      },
      social_linkedin: {
        label: "LinkedIn",
        placeholder: "https://linkedin.com/in/nomdusuari",
      },
      social_bluesky: {
        label: "Bluesky",
        placeholder: "https://bsky.app/profile/nomdusuari",
      },
      social_books: {
        label: "Llibres",
        placeholder: "https://goodreads.com/user/show/nomdusuari",
      },
      social_website: {
        label: "Web",
        placeholder: "https://latevaWeb.com",
      },
      social_soundcloud: {
        label: "SoundCloud",
        placeholder: "https://soundcloud.com/nomdusuari",
      },
      social_bandcamp: {
        label: "Bandcamp",
        placeholder: "https://nomdusuari.bandcamp.com",
      },
    },
    subscriptions: {
      empty: "Encara no tens subscripcions",
      unsubscribeTitle: "Cancel·lar subscripció",
      unsubscribeDescription: "Ja no rebràs actualitzacions d'aquesta publicació.",
      unsubscribePaidDisclaimer: "El teu pla de pagament actual també es cancel·larà.",
      holdToUnsubscribe: "Mantén per cancel·lar",
      unsubscribing: "Descrivint...",
      unsubscribed: "Subscripció cancel·lada",
    },
    deleteAccount: {
      title: "Eliminar compte",
      description: "Elimina permanentment el teu compte i totes les dades",
      warning: "Aquesta acció no es pot desfer. Tots els articles, notes i dades de subscriptors s'eliminaran permanentment.",
      button: "Eliminar compte",
      confirmTitle: "Eliminar el teu compte?",
      confirmDescription: "Això eliminarà permanentment el teu compte i tot el contingut. Aquesta acció no es pot desfer.",
      exportBeforeDeletePrefix: "Abans d'eliminar, pots",
      exportBeforeDeleteSuffix: "les dades de la teva publicació, incloent tots els articles, notes i informació de subscriptors.",
      typeToConfirm: "Escriu {text} per confirmar",
      confirmButton: "Eliminar el meu compte",
    },
  },
  post: {
    preview: "Vista prèvia",
    previewDescription: "Mira com es veuria la teva publicació per als teus lectors.",
    unarchive: "Desarxivar",
    update: "Actualitzar",
    updated: "Publicació actualitzada correctament",
    publish: "Continuar",
    uploadHeaderImage: "Puja imatges de capçalera (16:9, 800 × 450 px)",
    title: "Títol",
    subtitle: "Subtítol",
    statusSpam: "Aquesta versió està marcada com a correu brossa; contacta amb suport si creus que és un error.",
    statusArchived: "Aquesta versió està arxivada; la gent no la pot veure, però encara pots editar-la.",
    statusSent: "Aquesta versió s'ha enviat als teus subscriptors per correu",
    statusPublished: "Aquesta versió està publicada i és visible per als teus lectors",
    statusDraft: "Aquesta és una versió d'esborrany; en pots veure la previsualització però no és visible per als teus lectors",
    words: "paraules",
    savedAt: "Desat a les {time}",
    notes: "Notes",
    notesDescription: "Crea i fixa notes a aquest article.",
    proofread: "Revisa",
    proofreadDescription: "Obteniu ajuda per millorar la claredat i l'atractiu del vostre article.",
    versions: "Versions",
    settings: "Configuració",
    settingsDescription: "Configura la teva publicació.",
    translations: "Traduccions",
    alsoIn: "També en",
    translationsDescription: "Tradueix automàticament el teu article a altres idiomes. Arriba a més lectors.",
    share: "Comparteix",
    shareDescription: "Obtén un enllaç per compartir el teu article a les xarxes socials.",
    focusMode: "Mode concentració",
    focusModeDescription: "Converteix l'editor en un mode sense distraccions perquè et puguis concentrar en la teva escriptura.",
    translationOutdated: "Aquesta traducció es va fer per a una versió anterior",
    mainLanguage: "Idioma principal",
    alternativeLanguages: "Idiomes alternatius",
    alternativeLanguagesDescription: "Activa les traduccions per al teu post. Activa els idiomes per generar-les o desactiva'ls per eliminar-les.",
    translationsSaved: "Traduccions desades correctament",
    translationOutdatedMessage: "Aquesta traducció està desactualitzada.",
    translationUpToDate: "Aquesta traducció està actualitzada.",
    regenerateTranslation: "Actualitzar-la",
    translationOutdatedOr: "O",
    markTranslationAsUpdated: "Marcar com a actualitzada",
    updatingTranslation: "Actualitzant traducció...",
    translationRegenerated: "Traducció actualitzada correctament",
    saveChanges: "Desar canvis",
    saveAndGenerate: "Desar i generar",
    saveAndDelete: "Desar i eliminar",
    publishPage: {
      update: "Actualitzar",
      publish: "Publicar",
      delivery: "Lliurament",
      deliveryDescription: "Com vols lliurar aquesta publicació?",
      sendViaEmail: "Enviar per correu",
      alreadySent: "Ja has enviat aquesta publicació als teus subscriptors per correu el {date}",
      subscribersWillReceive: "{{Cap subscriptor|1 subscriptor|?? subscriptors}} rebran aquesta publicació a la seva bústia.",
      sendTestToMyself: "Enviar prova a mi mateix",
      testEmailSent: "Correu de prova enviat correctament",
      testEmailError: "Error en enviar el correu de prova",
      audienceTitle: "Audiència",
      audienceDescription: "Tria qui pot veure aquesta publicació.",
    },
  },
  editor: {
    defaultPlaceholder: "Escriu alguna cosa…",
    highlightColor: "Color de ressaltat",
    noHighlight: "Sense ressaltat",
    grey: "Gris",
    yellow: "Groc",
    blue: "Blau",
    green: "Verd",
    orange: "Taronja",
    red: "Vermell",
    bulletList: "Llista amb vinyetes",
    orderedList: "Llista numerada",
    image: "Imatge",
    code: "Codi",
    quote: "Cita",
    subscribe: "Subscriu-t'hi",
    imageTitlePlaceholder: "Títol de la imatge",
    imageDescriptionPlaceholder: "Descripció per a l'accessibilitat",
    imageSizeSmall: "Petit",
    imageSizeMedium: "Mitjà",
    imageSizeBig: "Amplada completa",
    turnIntoLabel: "Converteix en...",
    heading1: "Títol 1",
    heading2: "Títol 2",
    heading3: "Títol 3",
    blockquote: "Cita",
    text: "Text",
    callout: "Destacat",
    bold: "Negreta",
    italic: "Cursiva",
    strikethrough: "Ratllat",
    inlineCode: "Codi en línia",
    highlight: "Ressaltat",
    link: "Enllaç",
    horizontalRule: "Divisor",
    undo: "Desfer",
    redo: "Refer",
    insertBlock: "Inserir bloc",
    more: "Més",
    snippet: "Snippet",
    codeBlock: "Bloc de codi",
    math: "Math",
    embed: "Embed",
    embedDisplay: "Visualització",
    embedDisplayEmbed: "Interactiu",
    embedDisplayCard: "Targeta",
    language: "Idioma",
  },
  notes: {
    newNote: "Nova nota",
    cancel: "cancel·lar",
    noPinnedYet: "encara no tens cap nota",
    voice: "veu",
    copyToPost: "copia a l'article",
  },
  blog: {
    title: "Publicació",
    audience: "Audiència",
    forms: "Forms",
    stats: "Estadístiques",
    noResultsFound: "No s'han trobat resultats",
    noPostsYet: "Encara no has creat cap article.",
    filter: {
      spam: "Spam",
      archived: "Arxivat",
      sent: "Enviat",
      published: "Publicat",
      draft: "Esborrany",
    },
    blogName: "Nom de la publicació",
    blogNameDescription: "Estableix el nom de la teva publicació: s'utilitzarà per identificar-la i generar la URL",
    atLeast3Characters: "Usa de 3 a 15 caràcters: lletres, números o guions.",
    nameAvailable: "Aquest nom està disponible.",
    nameInvalid: "Aquest nom no és vàlid.",
    nameUnavailable: "Aquest nom ja està en ús.",
    nameNaughty: "Aquest nom no està permès.",
    nameChangeCooldown: "Podràs canviar el teu nom d'aquí a {days} {{dia|dies}}.",
    nameChangeWarning: "Després del canvi, no podràs canviar el teu nom durant 30 dies. La teva adreça antiga redirigirà a la nova.",
    checkingNameAvailability: "Comprovant disponibilitat del nom...",
    language: "Idioma",
    languageDescription: "Estableix l'idioma de la teva publicació",
    additionalLanguages: "Idiomes addicionals",
    additionalLanguagesDescription: "Tria quins idiomes estan disponibles per a les traduccions a la teva publicació. Tots els idiomes estan activats per defecte.",
    titleAndDescription: "Títol i descripció",
    titleAndDescriptionDescription: "Estableix el títol i la descripció del teu espai",
    yourBlogTitle: "el-títol-de-la-teva-publicació",
    description: "Descripció",
    yourBlogDescription: "la-descripció-de-la-teva-publicació",
    branding: "Marca",
    brandingDescription: "Afegeix la teva pròpia marca a la teva publicació i als correus",
    logo: "Logotip",
    logoDescription: "Es mostrarà a la part superior de la publicació i als correus. Mida recomanada: 200 x 200 píxels.",
    uploadLogo: "Puja el logotip",
    wordmark: "Marca de text",
    wordmarkDescription: "Es mostrarà en lloc del nom de la teva publicació a la part superior. Mida recomanada: 1000 x 200 píxels.",
    uploadWordmark: "Puja la marca de text",
    subscribeImage: "Imatge de subscripció",
    subscribeImageDescription: "Es mostrarà al formulari de subscripció. Mida recomanada: 600 x 600 píxels.",
    uploadSubscribeImage: "Puja la imatge de subscripció",
    favicon: "Favicon",
    faviconDescription: "Es mostrarà com a icona de la pestanya del navegador per a la teva publicació. Mida recomanada: 128 x 128 píxels.",
    uploadFavicon: "Puja el favicon",
    header: "Capçalera",
    headerDescription: "Afegeix una capçalera personalitzada a cadascun dels teus articles",
    footer: "Peu",
    footerDescription: "Afegeix un peu personalitzat a cadascun dels teus articles",
    welcomeEmail: {
      title: "Correu de benvinguda",
      description: "Personalitza el correu de benvinguda per a nous subscriptors",
      subjectLabel: "Assumpte del correu",
      subjectContent: "Contingut del correu",
    },
    export: {
      title: "Exportar",
      description: "Exporta tots els subscriptors i el contingut de la teva publicació",
      generateExport: "Generar exportació",
      generating: "Generant...",
      exportedAt: "Exportat el",
      subscribers: "Subscriptors",
      posts: "Publicacions",
      download: "Descarregar",
      processing: "Processant...",
      error: "Error",
      noExports: "Encara no hi ha exportacions",
    },
    subscribersPage: {
      email: "Correu electrònic",
      subscribedAt: "Subscrit el",
      tier: "Nivell",
      form: "Form",
      allForms: "All forms",
      direct: "Direct",
      free: "Gratuït",
      allTiers: "Tots els nivells",
      noSubscribersFound: "No s'han trobat subscriptors",
      noSubscribersYet: "Encara no hi ha subscriptors",
      publication: "Publicació",
      followBack: "Subscriure's",
      following: "Subscrit",
    },
    formsPage: {
      editorTitle: "Create form",
      editEditorTitle: "Edit form",
      createForm: "Create form",
      editForm: "Edit form",
      name: "Name",
      audience: "Destinataris",
      audienceDescription: "Tria qui pot rebre el correu amb els fitxers. Si restringeixes l'accés, primer s'hauran de subscriure o pujar de nivell.",
      downloads: "Files",
      subscribers: "Subscribers",
      size: "Size",
      actions: "Actions",
      everyone: "Tothom",
      everyoneDescription: "Qualsevol persona pot enviar el formulari i rebre el correu amb els fitxers.",
      subscribersOnly: "Només subscriptors",
      subscribersOnlyDescription: "Només els subscriptors poden rebre el correu amb els fitxers.",
      content: "Description",
      email: "Email",
      emailSubject: "Email subject",
      emailBody: "Email body",
      formSectionTitle: "Create form",
      formSectionDescription: "Set up your form and file delivery",
      filesSectionTitle: "Files",
      filesSectionDescription: "Upload the files readers will receive",
      emailSectionDescription: "Customize the delivery email sent after submit",
      defaultDescription: "Subscriu-te per rebre aquests arxius per correu electronic.",
      defaultEmailSubject: "Els teus arxius estan a punt",
      defaultEmailSubjectWithName: "{publicationName}: els teus arxius estan a punt",
      defaultEmailGreeting: "Hola,",
      defaultEmailIntro: "gracies per subscriure't. Els enllacos de descarrega son a sota.",
      defaultEmailOutro: "Si tens qualsevol dubte, respon aquest correu.",
      defaultEmailSignature: "- L'equip",
      defaultEmailSignatureWithName: "- {publicationName}",
      dropFiles: "Drop the files",
      formCreated: "Form created",
      formUpdated: "Form updated",
      formDeleted: "Form deleted",
      blankSlate: {
        title: "Capture, nurture, convert",
        growReadership: "Grow your readership",
        growReadershipDescription: "Capture more readers by offering exclusive content behind a subscription form.",
        convertReaders: "Convert readers into paid subscribers",
        convertReadersDescription: "Offer useful downloads and resources that turn readers into subscribers.",
        goBeyondText: "Go beyond text",
        goBeyondTextDescription: "Give your audience more by supplementing your written content with downloadable media and resources.",
      },
    },
    reactionEmojis: "Emojis de reacció",
    reactionEmojisDescription: "Tria quins emojis poden utilitzar els lectors per reaccionar als teus articles",
    addEmoji: "Afegir emoji",
    interval: "interval",
    referrer: "origen",
    visitors: "visitants",
    views: "visualitzacions",
    country: "país",
    channel: "canal",
  },
  categories: {
    culture: "Cultura",
    technology: "Tecnologia",
    business: "Negocis",
    usPolitics: "Política EUA",
    finance: "Finances",
    food: "Menjar",
    podcast: "Podcast",
    sports: "Esports",
    art: "Art",
    worldPolitics: "Política Mundial",
    healthPolitics: "Política Sanitària",
    news: "Notícies",
    fashionBeauty: "Moda i Bellesa",
    music: "Música",
    faith: "Fe",
    climate: "Clima",
    science: "Ciència",
    literature: "Literatura",
    fiction: "Ficció",
    health: "Salut",
    design: "Disseny",
    travel: "Viatges",
    parenting: "Criança",
    philosophy: "Filosofia",
    comics: "Còmics",
    international: "Internacional",
    crypto: "Cripto",
    history: "Història",
    humor: "Humor",
    education: "Educació",
    filmTv: "Cinema i TV",
  },
  readerOnboarding: {
    subscribedTo: "T'has subscrit a \"{publicationTitle}\"",
    selectInterests: "Quins temes t'interessen?",
    selectInterestsDescription: "Selecciona les categories sobre les quals t'agradaria llegir. Això ens ajuda a recomanar-te publicacions que t'encantaran.",
    continue: "Continua",
    skip: "Salta per ara",
  },
  onboarding: {
    buildAudience: "Construeix la teva audiència amb les teves regles",
    ownYourContent: "A fika ets propietari del que escrius i de la comunitat que es reuneix al seu voltant.",
    nameYourBlog: "Comença posant nom a la teva publicació; el teu propi racó a la web oberta i salvatge.",
    yourBlogNamePlaceholder: "el-nom-de-la-teva-publicació",
    createBlog: "Crea la publicació",
    continue: "Continua",
    updateBlog: "Actualitza la publicació",
    onboardingRabbitAlt: "Conill d'inici",
    chooseLanguage: {
      title: "En quins idiomes escriuràs?",
      description: "Tria l'idioma de la teva publicació i habilita els addicionals per a les traduccions.",
    },
    createFirstPost: {
      title: "Publica el teu primer article",
      description: "Comença a escriure el teu primer article i posa't en marxa amb Fika. Comparteix els teus pensaments amb el món!",
      descriptionWithDraft: "Continua escrivint el teu primer article i posa't en marxa amb Fika. Comparteix els teus pensaments amb el món!",
      ignore: "Ignorar",
      createPost: "Crear article",
      continueWriting: "Continuar escrivint",
    },
    createSecondPost: {
      title: "Publica el teu segon article",
      description: "Escriu un altre article i comparteix-lo amb els teus lectors.",
      descriptionWithDraft: "Continua escrivint el teu segon article.",
      ignore: "Ignorar",
      createPost: "Crear article",
      continueWriting: "Continuar escrivint",
      creating: "Creant...",
    },
    fillProfile: {
      title: "Completa el teu perfil",
      description: "Afegeix el teu nom, biografia i imatge de perfil per personalitzar la teva presència a Fika.",
      ignore: "Ignorar",
      editProfile: "Editar perfil",
    },
    setLanguage: {
      title: "Estableix l'idioma de la teva publicació",
      description: "Tria l'idioma principal de la teva publicació. Això ajuda els lectors a trobar el teu contingut.",
      ignore: "Ignorar",
      chooseLanguage: "Tria l'idioma",
    },
    fillPublication: {
      title: "Afegeix detalls de la publicació",
      description: "Dona un títol, descripció i logotip a la teva publicació per fer-la destacar.",
      ignore: "Ignorar",
      editDetails: "Editar detalls",
    },
    translateArticles: {
      title: "Tradueix \"{title}\"",
      description: "Afegeix traduccions als teus articles per arribar a lectors en diferents idiomes.",
      ignore: "Ignorar",
      translateLatestArticle: "Traduir article",
    },
  },
  postSettings: {
    title: "configuració",
    customPagesTitle: "pàgines personalitzades",
    customPagesDescription: "fes que aquest article aparegui a la barra de navegació",
    customPageLabel: "pàgina personalitzada",
    seoMetadataTitle: "metadades seo",
    seoMetadataDescription: "el títol i la descripció de l'article tal com apareixeran als resultats de cerca",
    seoMetadataSaved: "metadades desades",
    metaTitleLabel: "metatítol",
    metaTitlePlaceholder: "títol de la teva publicació",
    metaDescriptionLabel: "metadescripció",
    metaDescriptionPlaceholder: "descripció de la teva publicació",
    canonicalTitle: "canònica",
    canonicalDescription: "si la font original de l'article és un altre lloc web, pots especificar l'URL canònica aquí per evitar problemes de contingut duplicat.",
    canonicalUrlLabel: "url canònica",
    canonicalUrlSaved: "url canònica desada",
    pinSaved: "pàgina personalitzada desada",
    tagsTitle: "etiquetes",
    tagsDescription: "afegeix etiquetes al teu article",
    audienceSaved: "configuració d'audiència desada",
    audienceTitle: "Audiència",
    audienceDescription: "Tria qui pot accedir a aquest article.",
    audienceEveryoneLabel: "Tothom",
    audienceEveryoneDescription: "Qualsevol pot veure-ho, incloent visitants que no estan subscrits.",
    audienceAllSubscribersLabel: "Només subscriptors",
    audienceAllSubscribersDescription: "Qualsevol subscriptor pot veure aquest article.",
    commentsEnabledTitle: "Comments",
    commentsEnabledDescription: "Allow readers to comment on this post.",
    publishedAtTitle: "data de publicació",
    publishedAtDescription: "canvia la data en què aquest article es va publicar per primera vegada.",
    publishedAtLabel: "data de publicació",
    publishedAtSaved: "data de publicació desada",
    archivePostTitle: "arxivar article",
    unarchivePostTitle: "desarxivar article",
    archivePostDescription: "amaga aquest article de la teva publicació.",
    unarchivePostDescription: "mostra aquest article a la teva publicació.",
    archivePostSaved: "article arxivat",
    deletePostTitle: "eliminar article",
    deletePostDescription: "elimina aquest article completament.",
    deletePostWarning: "aquesta acció no es pot desfer.",
    deletePostSaved: "article eliminat",
    holdToDelete: "Mantén per eliminar",
    deleting: "Eliminant...",
    deleted: "Eliminat!",
  },
  postShare: {
    preview: "Vista prèvia",
    previewDescription: "Com apareixerà la teva publicació a les xarxes socials i als resultats de cerca",
    articleLink: "Enllaç de l'article",
    copyLink: "Copiar enllaç",
    linkCopied: "Enllaç copiat al porta-retalls",
    metaTitleLabel: "Meta títol",
    metaTitlePlaceholder: "El títol de la teva publicació",
    metaDescriptionLabel: "Meta descripció",
    metaDescriptionPlaceholder: "La descripció de la teva publicació",
    shareOnSocialMedia: "Compartir a xarxes socials",
    shareOn: "Compartir a {network}",
    publishedCongrats: "La teva publicació està en línia!",
    shareNow: "Ara comparteix-la amb el món! 🚀",
    backToPost: "Tornar a la publicació",
    setCoverImage: "Establir com a portada",
    resetCoverImage: "Restablir a l'original",
    coverImageUpdated: "Imatge de portada actualitzada",
    socialImages: "Imatges per a xarxes socials",
    socialImagesDescription: "Descarrega imatges per a xarxes socials",
    download: "Descarregar",
  },
  postVersions: {
    title: "versions",
    loading: "carregant versions...",
    failed: "error en carregar versions",
    failedVersion: "error en carregar versió",
    restored: "versió restaurada correctament",
    restore: "restaurar",
    language: "idioma",
    pills: {
      current: "actual",
      archived: "arxivada",
      sent: "enviada",
      published: "publicada",
    },
  },
  subscribe: {
    defaultText: "Subscriu-te a {userName} per rebre actualitzacions directament al teu correu",
    button: "Subscriu-t'hi",
    emailPlaceholder: "Introdueix el teu correu",
  },
  snippet: {
    placeholder: "Descriu el que vols crear...",
    placeholderUpdate: "Descriu el que vols canviar...",
    buttonCreate: "crea",
    buttonUpdate: "actualitza",
    buttonCreating: "Creant...",
    buttonUpdating: "Actualitzant...",
  },
  auth: {
    signInOrSignUp: "Inicia sessió",
    registrationsClosed: "Els registres estan tancats. Els usuaris existents poden iniciar sessió.",
    accountSuspendedTitle: "Compte suspès",
    accountSuspendedMessage: "El teu compte ha estat suspès. Contacta humans@fika.bar si creus que és un error.",
    continueWithGoogle: "Continua amb Google",
    continueWithLinkedIn: "Continua amb LinkedIn",
    signingIn: "Iniciant sessió...",
    or: "o",
    enterYourEmail: "Introdueix el teu correu",
    failedToSend: "Error en enviar el codi de verificació. Torna-ho a provar.",
    sending: "Enviant...",
    enterCode: "Introdueix el codi de 6 dígits que s'ha enviat a",
    verifying: "Verificant...",
    invalidCode: "Codi invàlid o caducat. Torna-ho a provar.",
    useDifferentEmail: "Utilitza un correu diferent",
    subscribeTo: "Subscriu-te a",
    thisBlog: "aquesta publicació",
    getUpdates: "Subscriu-te per rebre actualitzacions a la teva safata d'entrada",
    subscribe: "Subscriu-t'hi",
    somethingWentWrong: "Alguna cosa ha anat malament",
    disclaimerPrefix: "En enviar el formulari, acceptes la",
    disclaimerPrivacy: "política de privacitat",
    disclaimerAnd: "i acceptes els",
    disclaimerTerms: "termes del servei",
    tierSelectionTitleSubscribed: "Estàs subscrit a {publicationName}!",
    tierSelectionTitleSubscribedNoName: "Estàs subscrit!",
    subscriptionError: "S'ha produït un error. Si us plau, torna-ho a provar.",
    verifyEmailSuccess: "Correu electrònic verificat correctament!",
    emailNotVerified: "Si us plau, verifica el teu correu electrònic per realitzar aquesta acció",
    subscriptionConfirmationDescriptionBeforeAuthor: "Rebràs actualitzacions de ",
    subscriptionConfirmationDescriptionAfterAuthor: " directament a la teva safata d'entrada.",
    subscriptionConfirmationDescriptionNoAuthor: "Rebràs actualitzacions directament a la teva safata d'entrada.",
  },
  unsubscribe: {
    writerSuccess: "t'has donat de baixa",
    writerSuccessDescription: "ja no rebràs correus d'aquest escriptor. sempre pots tornar a subscriure't des del seu perfil.",
    marketingSuccess: "t'has donat de baixa",
    marketingSuccessDescription: "ja no rebràs correus promocionals de fika. pots gestionar les teves preferències a la configuració.",
    weeklyFeaturedSuccess: "t'has donat de baixa",
    weeklyFeaturedSuccessDescription: "ja no rebràs el correu setmanal d'articles destacats. pots tornar a activar-lo a la configuració.",
    error: "alguna cosa ha anat malament",
    invalidToken: "aquest enllaç de baixa és invàlid o ha caducat. si us plau, torna-ho a provar des d'un correu recent.",
    missingToken: "no s'ha proporcionat cap testimoni de baixa. si us plau, utilitza l'enllaç del teu correu.",
    genericError: "s'ha produït un error inesperat. si us plau, torna-ho a provar més tard.",
    invalidRequest: "petició invàlida",
    invalidRequestDescription: "aquesta pàgina requereix un enllaç de baixa vàlid des d'un correu.",
    signInToManage: "inicia sessió per gestionar les subscripcions",
  },
  subscriptions: {
    title: "Subscripcions",
    manage: "Gestiona",
    manageTitle: "Gestiona les subscripcions",
    viewOriginal: "Veure l'original",
    noPublishedPostsYet: "Encara no hi ha publicacions publicades",
    previousPost: "Article anterior",
    nextPost: "Article següent",
    following: "Seguint",
    subscribed: "Subscrit",
    renewsAt: "Es renova el {date}",
    upgradePlan: "Millorar pla",
    tierChangeError: "Error en canviar el pla",
    manageBilling: "Facturació i rebuts",
    subscribersOnly: "Només subscriptors",
    tableOfContents: "Taula de continguts",
  },
  discover: {
    readMore: "Llegir més",
    upgradeToRead: "Actualitza per llegir",
    humanContent: "Humà",
    humanContentTitle: "Contingut humà",
    humanContentDescription: "Fika té una alta confiança que aquest contingut va ser escrit per un humà amb poca o cap assistència de la IA.",
  },
  privatePost: {
    subscribeCta: "Subscriu-te",
    upgradeCta: "Actualitzar per desbloquejar",
  },
  applicationLayout: {
    loadingPhrases: {
      synchronizing: "sincronitzant...",
      hadronCollider: "muntant el col·lisionador d'hadrons...",
      reticulatingSplines: "reticulant splines...",
      wakingHamsters: "despertant els hàmsters...",
      consultingOracle: "consultant l'oracle...",
      aligningStars: "alineant els estels...",
      fluxCapacitor: "calibrant el condensador de flux...",
      traveling88mph: "viatjant a 88 mph...",
      whiteRabbit: "seguint el conill blanc...",
      reversingPolarity: "invertint la polaritat...",
      enhancingImage: "millorant la imatge...",
      enteringMatrix: "entrant a la matriu...",
      findingRing: "trobant l'anell únic...",
      dividingByZero: "dividint per zero...",
    },
  },
  proofreader: {
    title: "Corrector",
    editorTitle: "Editor",
    editorDescription: "Revisa el teu article per millorar la claredat i l'impacte.",
    principles: {
      grammar: {
        label: "Gramàtica",
        description: "Corregeix errors ortogràfics i gramaticals.",
        action: "Corregir gramàtica",
      },
      tighten: {
        label: "Condensar",
        description: "Fes el text més concís.",
        action: "Condensar text",
      },
      refine: {
        label: "Refinar",
        description: "Millora la claredat, el flux i l'impacte del text.",
        action: "Refinar text",
      },
    },
    noFixesFound: "No s'han trobat correccions",
    countFixes: "{{Cap correcció|1 correcció|?? correccions}}",
    countFixesFound: "{{Cap correcció trobada|1 correcció trobada|?? correccions trobades}}",
    scanning: "Escanejant...",
    percentComplete: "{percent}% completat",
    discard: "Descartar",
    acceptAll: "Acceptar tot",
    lenses: {
      title: "Lents",
      description: "Visualitza l'estructura de les frases per millorar el ritme i la llegibilitat.",
      off: "apagat",
      difficulty: "dificultat",
      difficultyDescription: "Mostra com de difícils són les teves frases. Les frases més difícils apareixen en colors més càlids. Per millorar, fes servir frases més curtes i paraules més simples.",
      variation: "variació",
      variationDescription: "Mostra com de variades són les longituds de les teves frases. Les frases monòtones apareixen en colors més càlids. Per millorar, fes servir una barreja de frases curtes i llargues.",
      legend: {
        difficulty: {
          orange: "Molt difícil de llegir",
          amber: "Difícil de llegir",
        },
        variation: {
          orange: "Molt monòtona",
          amber: "Monòtona",
        },
      },
      tips: {
        veryHard: "Aquesta frase és molt difícil de llegir. Prova de dividir-la en frases més curtes o fer servir paraules més simples.",
        hard: "Aquesta frase és difícil de llegir. Considera simplificar-la.",
        veryMonotonous: "Les teves frases són molt semblants en longitud. Varia el ritme barrejant frases curtes i llargues.",
        monotonous: "Aquesta frase és semblant a les del voltant. Prova de variar la longitud per millorar el flux.",
      },
    },
  },
  inbox: {
    title: "Safata d'entrada",
    message: "Missatge",
    empty: "Encara no hi ha cartes ni comentaris",
    emptyLetters: "Encara no hi ha cartes",
    emptyComments: "Encara no hi ha comentaris",
    emptyDescription: "Fes servir aquest espai per veure les cartes dels teus lectors i les converses amb els autors als quals estàs subscrit.",
    unread: "No llegits",
    read: "Llegits",
    inReplyTo: "En resposta a:",
    writeMessage: "Escriu un missatge...",
    submit: "Enviar",
    publishing: "Publicant...",
    you: "Tu",
    tabAll: "Tot",
    tabEmails: "Cartes",
    tabComments: "Comentaris",
    typePrivateEmail: "Carta privada",
    typePublicComment: "Comentari públic",
    andMore: "i {count} més",
  },
  comments: {
    title: "Comentaris",
    writeComment: "Escriu un comentari...",
    submit: "Publicar comentari",
    publishing: "Publicant...",
    reply: "Respondre",
    delete: "Eliminar",
    deleting: "Eliminant...",
    deleteConfirm: "Vols eliminar aquest comentari?",
    empty: "Encara no hi ha comentaris. Sigues el primer a comentar!",
    anonymousParticipant: "Un subscriptor",
    flaggedMessage: "El teu missatge ha estat marcat com a correu brossa o contingut inadequat i no s'ha pogut enviar.",
    author: "Autor",
    privateVisibility: "Visible només per a tu i els participants",
    sendAsPrivate: "Enviar com a carta privada",
    translateTo: "Tradueix a {locale}",
    showOriginal: "Mostra original ({locale})",
  },
} satisfies Translation

export default ca
